import { format } from 'date-fns'
import { CalendarIcon, ClockIcon } from 'lucide-react'
import styled from 'styled-components'
import { Flex, Text } from 'ui'
import { useShiftTime } from '../useShiftTime'
import { previewTokens } from './styles'

export interface OnDemandDeliveryScheduleProps {
  date?: Date
  startTimeLabel?: string
  startTimeValue?: string
  endTimeValue?: string
  timezone?: string
}

const ScheduleCard = styled(Flex)`
  box-sizing: border-box;
  width: 100%;
  min-height: 68px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 20px;
  padding: 12px 14px;
  border: 1px solid ${previewTokens.colors.border};
  border-radius: ${previewTokens.radii.inner};
  background: ${previewTokens.colors.surface};
`

const ScheduleRow = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
`

const ScheduleText = styled(Text)`
  margin: 0;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
`

const DurationText = styled(Text)`
  margin: 0;
  color: ${previewTokens.colors.muted};
  font-size: 12px;
  line-height: 16px;
`

const formatEstimatedDuration = (durationInMinutes: number) => {
  const hours = Math.floor(durationInMinutes / 60)
  const minutes = durationInMinutes % 60
  const durationParts = [
    hours > 0 ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : null,
    minutes > 0 ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : null,
  ]

  return durationParts.filter(Boolean).join(' ')
}

export const OnDemandDeliverySchedule = (
  props: OnDemandDeliveryScheduleProps
) => {
  const { date, startTimeLabel, startTimeValue, endTimeValue, timezone } = props
  const { totalDurationInMinutes } = useShiftTime({
    date,
    startTime: startTimeValue,
    endTime: endTimeValue,
    timezone,
  })
  const hasScheduleRow = Boolean(date && startTimeLabel)
  const hasEstimatedDuration =
    Boolean(date) &&
    typeof totalDurationInMinutes === 'number' &&
    Number.isFinite(totalDurationInMinutes) &&
    totalDurationInMinutes > 0
  const estimatedDuration = hasEstimatedDuration
    ? formatEstimatedDuration(totalDurationInMinutes)
    : null

  if (!hasScheduleRow && !estimatedDuration) return null

  return (
    <ScheduleCard>
      {date && startTimeLabel ? (
        <ScheduleRow>
          <CalendarIcon size={14} aria-hidden="true" />
          <ScheduleText>{format(date, 'EEE MMM d')}</ScheduleText>
          <ClockIcon size={14} aria-hidden="true" />
          <ScheduleText>Check-in: {startTimeLabel}</ScheduleText>
        </ScheduleRow>
      ) : null}
      {estimatedDuration ? (
        <DurationText>Estimated duration {estimatedDuration}</DurationText>
      ) : null}
    </ScheduleCard>
  )
}
